import React, { useMemo } from "react";

import { SOIL_PROPERTY_METRICS } from "consts";

import { ValidationRow } from "models";

import { Table } from "components";

import { useValidationData } from "pages/admin/soilSight/hooks/useValidationData.ts";

const formatValue = (value: number | null | undefined): string => {
  if (value == null) return "—";
  return Number.isInteger(value) ? String(value) : value.toFixed(3);
};

type LocationTableRow = { key: string; location: string; [propertyKey: string]: string };

type Props = { rows: ValidationRow[] };

export const ValidationLocations = ({ rows }: Props) => {
  const { locations, locationColorMap } = useValidationData(rows);

  const locationRows = useMemo(() => Object.fromEntries(locations.map((location) => [location, rows.find((r) => r.location === location)])), [locations, rows]);

  const tableData: LocationTableRow[] = locations.map((location) => {
    const row: LocationTableRow = { key: location, location };
    for (const { key } of SOIL_PROPERTY_METRICS) {
      row[key] = formatValue(locationRows[location]?.[key]);
    }
    return row;
  });

  const columns = [
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      fixed: "left" as const,
      sorter: (a: LocationTableRow, b: LocationTableRow) => a.location.localeCompare(b.location),
      render: (val: string) => (
        <span className="soil-sight-validation-overview-location-cell">
          <span className="soil-sight-validation-overview-location-dot" style={{ background: locationColorMap[val] ?? "#999" }} />
          {val}
        </span>
      ),
    },
    ...SOIL_PROPERTY_METRICS.map(({ key, label, unit }) => ({
      title: (
        <div>
          <div>{label}</div>
          {unit && <div className="soil-sight-table-th-unit">{unit}</div>}
        </div>
      ),
      dataIndex: key,
      key,
      align: "right" as const,
      sorter: (a: LocationTableRow, b: LocationTableRow) => {
        const av = parseFloat(a[key]);
        const bv = parseFloat(b[key]);
        return (Number.isNaN(av) ? -Infinity : av) - (Number.isNaN(bv) ? -Infinity : bv);
      },
    })),
  ];

  return (
    <Table
      data={tableData}
      columns={columns}
      size="small"
      pagination={false}
      rowKey="key"
      rowClassName={(_: LocationTableRow, index: number) => (index % 2 !== 0 ? "soil-sight-row--striped" : "")}
      scroll={{ x: true }}
    />
  );
};
